import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Stack, AppBar, Button, Box, Typography, Container } from '@mui/material';
import { bgBlur } from '../../../utils/cssStyles';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import { HEADER, NAV } from '../../../config-global';
import Logo from '../../../components/logo';
import { useStoresContext } from '../../../stores/useStoresContext';
import { NEW_USER, NEW_USER_UNCONFIRMED, RETURNING_USER } from '../../../constants/unauthenticated-user-reasons';
import { CREATING_ACCOUNT_DEFAULT, SIGNING_IN_DEFAULT } from '../../../constants/analytics-events';
import { handleEvent } from '../../../utils/handleEvent';
import { pxToRem } from '../../../theme/typography';
import { useSettingsContext } from '../../../components/settings';

Header.propTypes = {
  isNavHorizontal: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  sx: PropTypes.object,
  openNav: PropTypes.bool,  // Add this line
};

export default function Header({ isNavHorizontal, isAuthenticated, sx, openNav }) {  // Add openNav here
  const theme = useTheme();
  const { themeStretch } = useSettingsContext();
  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const { layoutStore } = useStoresContext();

  const handleAuthModalOpen = () => {
    layoutStore.openAuthModal({ flowType: "auth", isUserNew: RETURNING_USER });

    handleEvent({
      eventName: SIGNING_IN_DEFAULT,
      isDesktop: isDesktop,
    });
  };

  const handleRegisterModalOpen = () => {
    layoutStore.openAuthModal({ flowType: "auth", isUserNew: NEW_USER_UNCONFIRMED });

    handleEvent({
      eventName: CREATING_ACCOUNT_DEFAULT,
      isDesktop: isDesktop,
    });
  };

  const renderContent = (
    <>
      {isNavHorizontal && <Logo sx={{ mr: 2.5 }} noText={true} />}
      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          marginRight: isDesktop ? "16%" : "0",
        }}
        spacing={{ xs: 1, sm: 1.5 }}
      >
        {isAuthenticated ? (
          null
        ) : (
          <>
            <Button
              variant="outlined"
              size={isDesktop ? "medium" : "small"}
              onClick={handleAuthModalOpen}
            >
              Sign in
            </Button>
            <Button
              variant="contained"
              size={isDesktop ? "medium" : "small"}
              onClick={handleRegisterModalOpen}>
              Create account
            </Button>
          </>
        )}
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        display: isAuthenticated ? "none" : "flex",
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height', 'width'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: isAuthenticated ? `calc(100% - ${openNav ? NAV.W_DASHBOARD : NAV.W_DASHBOARD_MINI + 1}px)` : "100%",  // Adjust width based on openNav
          height: isAuthenticated ? HEADER.H_DASHBOARD_DESKTOP : `calc(${HEADER.H_DASHBOARD_DESKTOP} + ${pxToRem(32)})`,
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
        }),
        ...sx,
      }}

      data-qa-id={"app-bar"}
    >
      <Container
        maxWidth={themeStretch ? false : "lg"}
        sx={{
          height: "100%",
          padding: "0 !important",
          marginTop: pxToRem(120),
          zIndex: 10000,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "baseline",
        }}
        data-qa-id={"landing-header-section"}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'baseline',
            marginLeft: "16%",
          }}
          data-qa-id="landing-text-container"
        >

          <Typography
            sx={{
              fontSize: isDesktop ? pxToRem(40) : pxToRem(32),
              fontWeight: '700',
              lineHeight: 1.125,
              color: theme.palette.text.primary,
              fontFamily: theme.typography.fontFamilyLarge,
              width: "100%",
              // Force each nested span onto its own line:
              '& span': {
                display: 'block',
              },
            }}
            data-qa-id="hero-title"
          >
            <span>The OCD journal</span>
            <span>built for you</span>

          </Typography>
          <Typography
            paragraph
            sx={{
              color: '#78653E',
              fontSize: isDesktop ? pxToRem(18) : pxToRem(14),
              fontWeight: 500,
              marginTop: pxToRem(16),
              marginBottom: "0 !important",
            }}
            data-qa-id="hero-subheading"
          >
            <span sx={{ fontFamily: theme.typography.fontFamily, lineHeight: 1, }}>New prompts every day</span>
          </Typography>
        </Box>

        {renderContent}
      </Container>
    </AppBar>
  );
}
